import { Badge, Box, Button, Card, Stack, Table, Text, Title } from "@mantine/core";
import { FileDown } from "lucide-react";
import type { PortalInvoice } from "@/lib/api/billing";
import { INVOICE_STATUS, formatAmount, formatPeriod } from "@/lib/invoices";

export interface InvoicesViewProps {
  invoices: PortalInvoice[] | undefined;
  isLoading: boolean;
  /** HTTP status of a failed load (403 = not a tenant admin), or true for any other failure. */
  error: number | true | null;
  /** The invoice whose PDF is being fetched; every other PDF button waits for it. */
  downloadingId: number | null;
  onPdf: (invoice: PortalInvoice) => void;
}

/** The Invoices page body — presentational, so it renders without the API client. */
export function InvoicesView({ invoices, isLoading, error, downloadingId, onPdf }: InvoicesViewProps) {
  return (
    <Stack p="md" gap="lg">
      <Stack gap={4}>
        <Title order={2}>Invoices</Title>
        <Text fz="sm" c="dimmed">
          Your company&apos;s invoices and credit notes. Amounts include VAT.
        </Text>
      </Stack>
      <InvoicesBody
        invoices={invoices}
        isLoading={isLoading}
        error={error}
        downloadingId={downloadingId}
        onPdf={onPdf}
      />
    </Stack>
  );
}

export function InvoicesBody({ invoices, isLoading, error, downloadingId, onPdf }: InvoicesViewProps) {
  if (isLoading) return <Text fz="sm">Loading invoices…</Text>;
  if (error === 403) {
    return <Text fz="sm" c="dimmed">Only your company&apos;s admins can see invoices.</Text>;
  }
  if (error) {
    return <Text fz="sm" c="red">Invoices could not be loaded. Please try again later.</Text>;
  }
  if (!invoices?.length) {
    return (
      <Card withBorder padding="lg">
        <Text fz="sm" c="dimmed">No invoices yet.</Text>
      </Card>
    );
  }
  return (
    <Card withBorder padding={0}>
      <Table.ScrollContainer minWidth={720}>
        <Table verticalSpacing="sm" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Invoice</Table.Th>
              <Table.Th>Period</Table.Th>
              <Table.Th>Invoice date</Table.Th>
              <Table.Th ta="right">Amount</Table.Th>
              <Table.Th>Due date</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th aria-label="PDF" />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {invoices.map((invoice) => {
              const status = INVOICE_STATUS[invoice.status];
              return (
                <Table.Tr key={invoice.id} data-testid={`invoice-${invoice.id}`}>
                  <Table.Td>
                    <Text fz="sm" fw={500}>{invoice.number ?? "—"}</Text>
                    {invoice.kind === "credit_note" && invoice.credits_number ? (
                      <Text fz="xs" c="dimmed">Credits {invoice.credits_number}</Text>
                    ) : null}
                  </Table.Td>
                  <Table.Td>
                    <Text fz="sm">{formatPeriod(invoice.period)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fz="sm">{invoice.invoice_date ?? "—"}</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text fz="sm" style={{ whiteSpace: "nowrap" }}>
                      {formatAmount(invoice.amount, invoice.currency)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fz="sm">{invoice.due_date ?? "—"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={status.color} variant="light">{status.label}</Badge>
                  </Table.Td>
                  <Table.Td ta="right">
                    {invoice.has_pdf ? (
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<FileDown size={14} />}
                        loading={downloadingId === invoice.id}
                        disabled={downloadingId !== null && downloadingId !== invoice.id}
                        onClick={() => onPdf(invoice)}
                        aria-label={`Download PDF of ${invoice.number}`}
                      >
                        PDF
                      </Button>
                    ) : (
                      <Box />
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Card>
  );
}
